import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiErrorText } from '@/lib/api-error'
import { formCard } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { fetchOverview } from '../api/projects-phase-d.api'
import {
  NOTE_COLOR_CLASSES,
  POSITION_STATUS_LABELS,
  TRAFFIC_LIGHT_CLASSES,
  TRAFFIC_LIGHT_REASONS,
  alsTag,
  alsZeitpunkt,
} from '../project-labels'
import type { OverviewResponse } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'
import type { TrafficLightResult } from '@symphenia/shared-types'

/**
 * AP-35 Detailblatt 8 5.2 — **Der Reiter Übersicht.**
 *
 * Die drei Ampeln stehen oben, **immer mit Begründung**: Eine Ampel ohne Grund
 * ist eine Behauptung. Grau heißt „keine Quelle" — bei der Kosten-Ampel bis
 * Phase I der Normalfall, und der Tooltip sagt genau das, statt Grün zu
 * suggerieren.
 */
export function ProjectOverviewTab({ projekt }: { projekt: ProjectDetail }) {
  const terms = useTerminology()
  const [daten, setDaten] = useState<OverviewResponse | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchOverview(projekt.id))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projekt.id])

  useEffect(() => {
    void laden()
  }, [laden])

  if (fehler) return <div className="text-sm text-destructive">{fehler}</div>
  if (!daten) return <div className="text-sm text-muted-foreground">Lädt …</div>

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Ampel titel="Termin" wert={daten.trafficLights.schedule} />
        <Ampel titel="Kosten" wert={daten.trafficLights.cost} />
        <Ampel titel="Leistungsstand" wert={daten.trafficLights.progress} zusatz={`${daten.progress} %`} />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className={formCard}>
          <h3 className="mb-2 text-sm font-medium">Phasen</h3>
          {daten.phases.length === 0 ? (
            <p className="text-xs text-muted-foreground">Keine Phasen belegt.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] table-fixed text-sm">
                <colgroup>
                  <col />
                  <col className="w-24" />
                  <col className="w-24" />
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className={kopfZelle}>Phase</th>
                    <th className={kopfZelle}>von</th>
                    <th className={kopfZelle}>bis</th>
                    <th className={kopfZelle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {daten.phases.map((p) => (
                    <tr key={p.phaseId} className="border-t border-border/60">
                      <td className={zelle}>
                        <span className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ background: p.color ?? 'hsl(var(--muted-foreground))' }}
                            aria-hidden
                          />
                          <span className="truncate" title={p.name}>
                            {p.name}
                          </span>
                        </span>
                      </td>
                      <td className={`${zelle} tabular-nums text-muted-foreground`}>{alsTag(p.startsAt)}</td>
                      <td className={`${zelle} tabular-nums text-muted-foreground`}>{alsTag(p.endsAt)}</td>
                      <td className={zelle}>
                        <PhasenStatus fixiert={p.isFixed} erledigt={!!p.doneAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={formCard}>
          <h3 className="mb-2 text-sm font-medium">{terms.positionPlural}</h3>
          {daten.positions.length === 0 ? (
            <p className="text-xs text-muted-foreground">Noch keine {terms.positionPlural}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] table-fixed text-sm">
                <colgroup>
                  <col className="w-10" />
                  <col />
                  <col className="w-32" />
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className={kopfZelle}>Nr.</th>
                    <th className={kopfZelle}>{terms.position}</th>
                    <th className={kopfZelle}>Fortschritt</th>
                    <th className={kopfZelle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {daten.positions.map((p) => (
                    <tr key={p.id} className="border-t border-border/60">
                      <td className={`${zelle} font-mono text-xs text-muted-foreground`}>{p.number}</td>
                      <td className={zelle}>
                        <span className="block truncate" title={p.name}>
                          {p.name}
                        </span>
                      </td>
                      <td className={zelle}>
                        <span className="flex items-center gap-2">
                          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <span
                              className="block h-full bg-primary"
                              style={{ width: `${Math.min(100, p.progress.percent)}%` }}
                            />
                          </span>
                          <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                            {p.progress.percent} %
                          </span>
                        </span>
                      </td>
                      <td className={zelle}>
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[11px] ${POSITION_STATUS_LABELS[p.status]?.class ?? ''}`}
                        >
                          {POSITION_STATUS_LABELS[p.status]?.label ?? p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={formCard}>
          <h3 className="mb-2 text-sm font-medium">Offen</h3>
          <ul className="space-y-1 text-sm">
            {daten.open.waitingForWorkStepRelease > 0 && (
              <li>
                {daten.open.waitingForWorkStepRelease} Arbeitsschritte warten auf Freigabe
              </li>
            )}
            {daten.open.tasks.map((a) => (
              <li key={a.id} className={a.state === 'OVERDUE' ? 'text-destructive' : ''}>
                {a.title}
                {a.dueAt && <span className="text-xs text-muted-foreground"> · fällig {alsTag(a.dueAt)}</span>}
              </li>
            ))}
            {daten.open.tasks.length === 0 && daten.open.waitingForWorkStepRelease === 0 && (
              <li className="text-xs text-muted-foreground">Nichts offen.</li>
            )}
          </ul>
        </div>

        <div className={formCard}>
          <h3 className="mb-2 text-sm font-medium">Angepinnt</h3>
          <ul className="space-y-2">
            {daten.pinned.length === 0 && <li className="text-xs text-muted-foreground">Nichts angepinnt.</li>}
            {daten.pinned.map((e) => (
              <li key={e.id} className={`rounded-md border p-2 text-sm ${NOTE_COLOR_CLASSES[e.color ?? 'YELLOW']}`}>
                <p className="whitespace-pre-line">{e.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className={formCard}>
        <h3 className="mb-2 text-sm font-medium">Zuletzt</h3>
        <ul className="space-y-1 text-sm">
          {daten.recent.map((e) => (
            <li key={e.id} className={e.isSystem ? 'text-muted-foreground' : ''}>
              <span className="mr-2 text-xs text-muted-foreground">{alsZeitpunkt(e.occurredAt)}</span>
              {e.text}
            </li>
          ))}
          {daten.recent.length === 0 && <li className="text-xs text-muted-foreground">Noch nichts geschehen.</li>}
        </ul>
        <Link to={`/projects/${projekt.id}`} className="sr-only">
          {projekt.projectNumber}
        </Link>
      </div>
    </div>
  )
}

/**
 * Phasen und Positionen stehen als Tabelle mit festen Spaltenbreiten
 * (`table-fixed` + `colgroup`): Datum, Fortschritt und Status liegen in jeder
 * Zeile an derselben Stelle und lassen sich senkrecht vergleichen. Wird die
 * Karte zu schmal, scrollt die Tabelle waagrecht, statt umzubrechen.
 */
const kopfZelle = 'pb-1 pr-2 font-normal last:pr-0'
const zelle = 'py-1 pr-2 align-middle last:pr-0'

/** Status einer Phase: erledigt und fixiert sind unabhängig, daher bis zu zwei Marken. */
function PhasenStatus({ fixiert, erledigt }: { fixiert: boolean; erledigt: boolean }) {
  if (!fixiert && !erledigt) return <span className="text-xs text-muted-foreground">offen</span>
  return (
    <span className="flex flex-wrap gap-1">
      {erledigt && (
        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] text-green-800 dark:bg-green-900 dark:text-green-200">
          erledigt
        </span>
      )}
      {fixiert && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">fixiert</span>
      )}
    </span>
  )
}

function Ampel({ titel, wert, zusatz }: { titel: string; wert: TrafficLightResult; zusatz?: string }) {
  const grund = TRAFFIC_LIGHT_REASONS[wert.reason] ?? wert.reason
  return (
    <div className={formCard} title={grund}>
      <div className="flex items-center gap-2">
        <span className={`h-3 w-3 rounded-full ${TRAFFIC_LIGHT_CLASSES[wert.value]}`} aria-hidden />
        <span className="text-sm font-medium">{titel}</span>
        {zusatz && <span className="ml-auto text-sm text-muted-foreground">{zusatz}</span>}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{grund}</p>
    </div>
  )
}
